import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Icon } from "@iconify/react";
import bellFill from "@iconify/icons-eva/bell-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import alertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import playCircleFill from "@iconify/icons-eva/play-circle-fill";
import pauseCircleFill from "@iconify/icons-eva/pause-circle-fill";
import starFill from "@iconify/icons-eva/star-fill";
import doneAllFill from "@iconify/icons-eva/done-all-fill";
// material
import { alpha } from "@material-ui/core/styles";
import {
  Box,
  List,
  Badge,
  Button,
  Divider,
  ListItem,
  IconButton,
  Typography,
  ListItemText,
  ListSubheader,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
} from "@material-ui/core";
// components
import Scrollbar from "../../components/Scrollbar";
import MenuPopover from "../../components/MenuPopover";
import moment from "moment";
import { useSelector } from "react-redux";
import { tareasApi } from "src/services/tareasApi";
import { propuestasApi } from "src/services/propuestasApi";

// Configuración de iconos por tipo de notificación
const getNotificationIcon = (tipo, categoria) => {
  const iconMap = {
    tareas: {
      vencidas: alertTriangleFill,
      proximasVencer: clockFill,
      sinIniciar: playCircleFill,
      estancadas: pauseCircleFill,
      sinFechaLimite: starFill,
    },
    propuestas: {
      proximasExpirar: clockFill,
      caducadas: alertTriangleFill,
      sinEnviar: starFill,
      conActividad: bellFill,
    },
  };
  return iconMap[tipo]?.[categoria] || bellFill;
};

// Configuración de colores por prioridad
const getNotificationColor = (categoria) => {
  const colorMap = {
    vencidas: "error.main",
    caducadas: "error.main",
    proximasVencer: "warning.main",
    proximasExpirar: "warning.main",
    estancadas: "warning.main",
    sinIniciar: "info.main",
    sinEnviar: "info.main",
    conActividad: "success.main",
    sinFechaLimite: "text.secondary",
  };
  return colorMap[categoria] || "text.secondary";
};

// Función para calcular tiempo relativo
function calcularTiempoRelativo(
  fecha,
  esTimestamp = false,
  esFechaVacia = false,
) {
  if (esFechaVacia || !fecha || fecha === "") {
    return "Sin fecha límite";
  }

  const fechaMoment = esTimestamp
    ? moment.unix(fecha)
    : moment(fecha, "YYYY/MM/DD", true);
  const ahora = moment();

  if (fechaMoment.isSame(ahora, "day")) {
    return "Hoy";
  } else if (fechaMoment.isSame(ahora.subtract(1, "day"), "day")) {
    return "Ayer";
  } else if (fechaMoment.isAfter(ahora)) {
    const dias = fechaMoment.diff(ahora, "days");
    return dias === 1 ? "En 1 día" : `En ${dias} días`;
  } else {
    const dias = ahora.diff(fechaMoment, "days");
    return dias === 1 ? "Hace 1 día" : `Hace ${dias} días`;
  }
}

// Componente para items de tareas
function NotificationItemTarea({ notification, categoria }) {
  const icon = getNotificationIcon("tareas", categoria);
  const color = getNotificationColor(categoria);

  const getSubtitle = () => {
    switch (categoria) {
      case "vencidas":
        return `Vencida ${calcularTiempoRelativo(notification.fechaLimite)}`;
      case "proximasVencer":
        return `Vence ${calcularTiempoRelativo(notification.fechaLimite)}`;
      case "estancadas":
        return `En proceso ${calcularTiempoRelativo(notification.fechaInicio)}`;
      case "sinFechaLimite":
        return `Iniciada ${calcularTiempoRelativo(
          notification.fechaInicio,
        )} - Sin fecha límite`;
      default:
        return calcularTiempoRelativo(notification.fechaInicio);
    }
  };

  const getPrioridadChip = () => {
    if (categoria === "vencidas") {
      return (
        <Chip label="Vencida" size="small" color="error" variant="outlined" />
      );
    }
    if (categoria === "sinFechaLimite") {
      return (
        <Chip
          label="Sin fecha"
          size="small"
          color="default"
          variant="outlined"
        />
      );
    }
    return null;
  };

  return (
    <ListItem
      button
      to={`/dashboard/tareas/${notification.id}`}
      disableGutters
      component={RouterLink}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Box
        component={Icon}
        icon={icon}
        sx={{
          mr: 2,
          width: 20,
          height: 20,
          color: color,
          flexShrink: 0,
        }}
      />
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }} noWrap>
              {notification.nombre}
            </Typography>
            {getPrioridadChip()}
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Cliente: {notification.nombreCliente}
            </Typography>
            <Typography variant="caption" color="text.disabled" component="div">
              {getSubtitle()}
            </Typography>
          </Box>
        }
        disableTypography
      />
    </ListItem>
  );
}

// Componente para items de propuestas
function NotificationItemPropuesta({ notification, categoria }) {
  const icon = getNotificationIcon("propuestas", categoria);
  const color = getNotificationColor(categoria);

  const getSubtitle = () => {
    switch (categoria) {
      case "proximasExpirar":
        return `Expira ${calcularTiempoRelativo(notification.exp, true)}`;
      case "caducadas":
        return `Caducó ${calcularTiempoRelativo(notification.exp, true)}`;
      case "sinEnviar":
        return `Creada ${calcularTiempoRelativo(notification.fechaCreacion)}`;
      case "conActividad":
        return "Vista recientemente";
      default:
        return calcularTiempoRelativo(notification.fechaCreacion);
    }
  };

  return (
    <ListItem
      button
      to={`/dashboard/propuestas/${notification.tipo}/${notification.id}`}
      disableGutters
      component={RouterLink}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Box
        component={Icon}
        icon={icon}
        sx={{
          mr: 2,
          width: 20,
          height: 20,
          color: color,
          flexShrink: 0,
        }}
      />
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }} noWrap>
              {notification.nombreCliente}
            </Typography>
            {categoria === "caducadas" && (
              <Chip
                label="Caducada"
                size="small"
                color="error"
                variant="outlined"
              />
            )}
            {categoria === "conActividad" && (
              <Chip
                label="Nueva actividad"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Propuesta {notification.tipo.toLowerCase()}
            </Typography>
            <Typography variant="caption" color="text.disabled" component="div">
              {getSubtitle()}
            </Typography>
          </Box>
        }
        disableTypography
      />
    </ListItem>
  );
}

// Componente principal
export default function NotificationsPopover() {
  const { empleado: user } = useSelector((state) => state.auth.user);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Queries para obtener notificaciones
  const { data: notificacionesTareas = {}, isLoading: cargandoTareas } =
    tareasApi.useGetNotificacionesTareasQuery(user.rut);

  const { data: notificacionesPropuestas = {}, isLoading: cargandoPropuestas } =
    propuestasApi.useGetNotificacionesPropuestasQuery(user.rut);

  // Calcular totales
  const totalTareas = notificacionesTareas?.resumen?.totalNotificaciones || 0;
  const totalPropuestas =
    notificacionesPropuestas?.resumen?.totalNotificaciones || 0;
  const totalNotificaciones = totalTareas + totalPropuestas;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Función para renderizar sección de tareas
  const renderSeccionTareas = (tareas, categoria, titulo) => {
    if (!tareas || tareas.length === 0) return null;

    return (
      <Box key={categoria}>
        <ListSubheader
          disableSticky
          sx={{ py: 1, px: 2.5, typography: "overline" }}
        >
          {titulo} ({tareas.length})
        </ListSubheader>
        {tareas.slice(0, 5).map((tarea) => (
          <NotificationItemTarea
            key={tarea.id}
            notification={tarea}
            categoria={categoria}
          />
        ))}
        {tareas.length > 5 && (
          <Box sx={{ px: 2.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              +{tareas.length - 5} más...
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Función para renderizar sección de propuestas
  const renderSeccionPropuestas = (propuestas, categoria, titulo) => {
    if (!propuestas || propuestas.length === 0) return null;

    return (
      <Box key={categoria}>
        <ListSubheader
          disableSticky
          sx={{ py: 1, px: 2.5, typography: "overline" }}
        >
          {titulo} ({propuestas.length})
        </ListSubheader>
        {propuestas.slice(0, 5).map((propuesta) => (
          <NotificationItemPropuesta
            key={propuesta.id}
            notification={propuesta}
            categoria={categoria}
          />
        ))}
        {propuestas.length > 5 && (
          <Box sx={{ px: 2.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              +{propuestas.length - 5} más...
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        color={open ? "primary" : "default"}
        sx={{
          ...(open && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.focusOpacity,
              ),
          }),
        }}
      >
        <Badge badgeContent={totalNotificaciones} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{
          width: 380,
          maxHeight: "80vh",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notificaciones</Typography>
            <Typography variant="caption" color="text.secondary">
              {totalNotificaciones} pendientes
            </Typography>
          </Box>

          <IconButton color="primary" size="small">
            <Icon icon={doneAllFill} width={20} height={20} />
          </IconButton>
        </Box>

        <Divider />

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={`Tareas (${totalTareas})`} sx={{ fontSize: "0.75rem" }} />
          <Tab
            label={`Propuestas (${totalPropuestas})`}
            sx={{ fontSize: "0.75rem" }}
          />
        </Tabs>

        <Scrollbar
          sx={{
            height: 320,
            maxHeight: "60vh",
          }}
        >
          {/* Tab de Tareas */}
          {tabValue === 0 && (
            <List disablePadding>
              {cargandoTareas ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {renderSeccionTareas(
                    notificacionesTareas?.tareasVencidas,
                    "vencidas",
                    "Vencidas",
                  )}
                  {renderSeccionTareas(
                    notificacionesTareas?.tareasProximasVencer,
                    "proximasVencer",
                    "Próximas a vencer",
                  )}
                  {renderSeccionTareas(
                    notificacionesTareas?.tareasEstancadas,
                    "estancadas",
                    "Estancadas",
                  )}
                  {renderSeccionTareas(
                    notificacionesTareas?.tareasSinIniciar,
                    "sinIniciar",
                    "Sin iniciar",
                  )}

                  {renderSeccionTareas(
                    notificacionesTareas?.tareasSinFechaLimite,
                    "sinFechaLimite",
                    "Sin fecha límite",
                  )}

                  {totalTareas === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay notificaciones de tareas
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </List>
          )}

          {/* Tab de Propuestas */}
          {tabValue === 1 && (
            <List disablePadding>
              {cargandoPropuestas ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {renderSeccionPropuestas(
                    notificacionesPropuestas?.propuestasExpiradas,
                    "caducadas",
                    "Caducadas",
                  )}
                  {renderSeccionPropuestas(
                    notificacionesPropuestas?.propuestasProximasExpirar,
                    "proximasExpirar",
                    "Próximas a expirar",
                  )}
                  {renderSeccionPropuestas(
                    notificacionesPropuestas?.propuestasConActividad,
                    "conActividad",
                    "Con actividad reciente",
                  )}
                  {renderSeccionPropuestas(
                    notificacionesPropuestas.propuestasSinEnviar,
                    "sinEnviar",
                    "Sin enviar",
                  )}

                  {totalPropuestas === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay notificaciones de propuestas
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </List>
          )}
        </Scrollbar>

        <Divider />

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            disableRipple
            component={RouterLink}
            to={
              tabValue === 0
                ? "/dashboard/tareas/"
                : "/dashboard/propuestas/listar"
            }
          >
            Ver todas
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

NotificationItemTarea.propTypes = {
  notification: PropTypes.object.isRequired,
  categoria: PropTypes.string.isRequired,
};

NotificationItemPropuesta.propTypes = {
  notification: PropTypes.object.isRequired,
  categoria: PropTypes.string.isRequired,
};
